/**
 * @file Base component to displaying forecast data
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef } from '@angular/core';
import { EntityQuery } from 'breeze-client';
import * as moment from 'moment';
import { PluginViewBase } from '../../grids/plugins/plugin-view/plugin-view.base';
import { Processor } from '../../grids/processors/processor';
import { Shrinkage } from '../helpers/shrinkage';
/**
 * The name of the end-point for retrieving prognoses.
 */
export var PROGNOSES = 'Prognoses';
/**
 * The name of the end-point for retrieving forecast attendants.
 */
export var PLANITEMS = 'ForecastPlanItems';
/**
 * The name of the end-point for retrieving calls.
 */
export var CALLS = 'Calls';
/**
 * The name of the end-point for retrieving opening hours.
 */
export var OPENING_HOURS = 'OpeningHours';
/**
 * The name of the end-point for retrieving special days.
 */
export var SPECIAL_DAYS = 'SpecialDays';
/**
 * The name of the end-point for retrieving holidays.
 */
export var HOLIDAYS = 'Holidays';
/**
 * The name of the end-point for retrieving correction factors.
 */
export var CORRECTIONS = 'Corrections';
/**
 * The name of the end-point for retrieving shrinkages.
 */
export var SHRINKAGES = 'Shrinkages';
/**
 * The class can be overloaded by plugin components that display forecast data.
 * @export
 * @class PluginForecastBase
 * @extends {PluginViewBase}
 * @implements {AfterViewInit}
 */
var PluginViewForecastBase = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewForecastBase, _super);
    /**
     * Creates an instance of PluginCallsComponent.
     * @param {LocalisationService} localisationService The localisation service.
     * @param {ChangeDetectorRef} changeDetector The change detector is used to update the rendered layout.
     * @param {EventsService} eventsService The events service.
     * @param {SettingsService} settingsService The settings service.
     * @param {ForecastService} forecastService The forecast service is used to retrieve prognoses.
     * @param {AlertsService} alertsService The alerts service.
     * @memberOf PluginViewForecastBase
     */
    function PluginViewForecastBase(localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService) || this;
        _this.gridService = gridService;
        _this.settingsService = settingsService;
        _this.forecastService = forecastService;
        _this.alertsService = alertsService;
        /**
         * The names of the queries that are loaded when the plugin view refreshes.
         * @protected
         * @type {string[]}
         * @memberOf PluginViewForecastBase
         */
        _this.queryNames = [];
        /**
         * The queries are loaded on a refresh.
         * @protected
         * @type {{ [id: string]: EntityQuery }}
         * @memberOf PluginViewForecastBase
         */
        _this.queries = {};
        /**
         * The results of the queries above are stored here, on the same index keys as the queries.
         * @protected
         * @type {{ [id: string]: Entity[] }}
         * @memberOf PluginViewForecastBase
         */
        _this.queryResults = {};
        /**
         * A value indicating whether the data is being loaded.
         * @memberOf PluginViewForecastBase
         */
        _this.loadingInProgress = false;
        _this.subscriptionPool.add(_this.eventsService.register('plugin-selector-changed').subscribe(function (result) {
            _this.refresh();
        }));
        return _this;
    }
    Object.defineProperty(PluginViewForecastBase.prototype, "options", {
        /**
         * Sets the shift.
         * @type {Entity}
         * @memberOf PluginViewForecastBase
         */
        set: function (value) {
            if (this.selectedFunction === value) {
                return;
            }
            this.selectedFunction = value;
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewForecastBase.prototype, "pluginKey", {
        /**
         * Gets the plugin key. The key to store and find the timeslot collections of this plugin.
         * @readonly
         * @public
         * @type {string}
         * @memberOf PluginViewBase
         */
        get: function () {
            return Processor.pluginKey + '-view-forecast';
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets the shift template, if the function and entity manager are known, and refreshes the plugin.
     * @private
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this.loadingInProgress = true;
                if (!this.selectedFunction) {
                    // Clear view.
                    this.gridCollections = this.generateGridSegments();
                    this.loadingInProgress = false;
                    this.detectChanges();
                    return [2 /*return*/, null];
                }
                this.setQueries();
                return [2 /*return*/, this.prepareBuild()];
            });
        });
    };
    /**
     * Loads the prognoses and resets the grid collection
     * @returns {Observable<any>}
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.prepareBuild = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                this.gridCollections = this.generateGridSegments();
                this.detectChanges(); // For loading indicator.
                switch (this.gridService.level.name) {
                    case 'day':
                        this.barWidth = moment.duration(this.selectedFunction['TimeslotLength']);
                        break;
                    case 'week':
                        this.barWidth = moment.duration(1, 'hours');
                        break;
                    default:
                        this.barWidth = null;
                        break;
                }
                result = new Promise(function (resolve, reject) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                    var response, reason_1;
                    return tslib_1.__generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                _a.trys.push([0, 2, , 3]);
                                return [4 /*yield*/, this.executeQueries()];
                            case 1:
                                response = _a.sent();
                                this.build();
                                resolve(response);
                                return [3 /*break*/, 3];
                            case 2:
                                reason_1 = _a.sent();
                                reject(reason_1);
                                return [3 /*break*/, 3];
                            case 3:
                                this.loadingInProgress = false;
                                this.detectChanges();
                                return [2 /*return*/];
                        }
                    });
                }); });
                return [2 /*return*/, result];
            });
        });
    };
    /**
     * Returns an object containing the start and end time of the chart and the width of the first chart bar.
     * @protected
     * @param {GridColumn} gridColumn The column in which the chart is placed.
     * @returns {ChartDimensions} The object contains the chart dimensions.
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.getBarDimensions = function (gridColumn) {
        var width;
        var startTime = gridColumn.meta.start.clone();
        if (this.barWidth) {
            width = this.barWidth;
        }
        else {
            var hours = this.settingsService.settings.frontend.grid.officeHours[this.gridService.officeHours ? 'enabled' : 'disabled'];
            width = moment.duration(hours.end - hours.start, 'hours');
        }
        var endTime = startTime.clone().add(width);
        return {
            startTime: startTime, endTime: endTime, width: width
        };
    };
    /**
     * Returns a string representation of the number with up to two decimals.
     * @protected
     * @param {number} value A string representation of the parameter is returned.
     * @returns {string} The value parameter in string format.
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.toMaxTwoDecimals = function (value) {
        var result = value.toFixed(2);
        if (result.indexOf('.00') !== -1) {
            return result.slice(0, result.indexOf('.00'));
        }
        else if (result.slice(-1) === '0') {
            return result.slice(0, result.length - 1);
        }
        return result;
    };
    /**
     * Sets the query that all forecast plugin views need. Can be extended in the views themselves.
     * @private
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.setQueries = function () {
        var _this = this;
        this.queryNames.forEach(function (name) {
            var query = EntityQuery.from(name);
            query.parameters = { oid: _this.selectedFunction['Oid'], start: _this.horizonStart, finish: _this.horizonFinish };
            _this.queries[name] = query;
        });
    };
    /**
     * Execute the queries required to calculate and display data in the plugin.
     * @private
     * @returns {Promise<QueryResult>} A promise telling when the queries are done loading.
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.prototype.executeQueries = function () {
        var _this = this;
        var promises = [];
        Object.keys(this.queries).forEach(function (key) {
            _this.queryResults[key] = null;
            promises.push(_this.forecastService.entityManager.executeQuery(_this.queries[key]).then(function (response) {
                if (key === SHRINKAGES) {
                    _this.queryResults[key] = [];
                    response.results.forEach(function (entity) { return _this.queryResults[key].push(new Shrinkage(entity)); });
                }
                else {
                    _this.queryResults[key] = response.results;
                }
            }).catch(function (error) {
                _this.alertsService.createAlert('warning', 'APP.PLUGINS.QUERY_ERROR');
                console.warn(error);
            }));
        });
        return Promise.all(promises);
    };
    /**
     * The back-end enumerator IsoDayOfWeek returns days of the week as string.
     * This array can be used to numeric values to those.
     * @protected
     * @static
     * @type {{ [index: string]: number }}
     * @memberOf PluginViewForecastBase
     */
    PluginViewForecastBase.daysOfWeek = {
        0: 'Monday',
        1: 'Tuesday',
        2: 'Wednesday',
        3: 'Thursday',
        4: 'Friday',
        5: 'Saturday',
        6: 'Sunday'
    };
    return PluginViewForecastBase;
}(PluginViewBase));
export { PluginViewForecastBase };
